import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePropertyRatesLoading,
  getPropertyCombinedRatesByRoom,
  getPropertyMappedBedbankOffer,
  getPropertySelectedRoomCombinedRates,
} from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AlwaysOnAboutRoomModalTab from './AlwaysOnAboutRoomModalTab'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import zIndex from 'styles/tools/z-index'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyRoomOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomOptionMobile'
import { isHotelPackage } from 'lib/offer/offerTypes'
import uuidV4 from 'lib/string/uuidV4Utils'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

const StickyTabButtonsGroup = styled(TabButtonsGroup)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.HotelOffer
  bedbankOffer?: App.BedbankOffer
  selectedRoom: RoomRateViews | undefined
  combinedRatesByRoom: Array<RoomRateViews>
  airports?: Array<App.Airport>
  isLoading: boolean
  onClose?: () => void
}

function SinglePropertyRoomAndPackageDetailsModal(props: Props) {
  const {
    offer,
    bedbankOffer,
    selectedRoom,
    combinedRatesByRoom,
    airports,
    isLoading,
    onClose,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const [selectedTab, setSelectedTab] = useState<'package-options' | 'about-room'>('package-options')

  const pkg = selectedRoom?.propertyRoom
  const isLpcPackage = isHotelPackage(pkg)
  const roomName = (isLpcPackage ? pkg.roomType.name : pkg?.name) ?? ''
  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedRatesByRoom]) // regenerate id if list of rooms changes

  const selectPackageOptionsTab = useCallback(() => {
    setSelectedTab('package-options')
  }, [])

  const selectAboutRoomTab = useCallback(() => {
    setSelectedTab('about-room')
  }, [])

  if (!pkg) return null

  return (<>
    <ModalHeader
      title={roomName}
      dismissible={false}
      onBackButtonClick={onClose}
    >
      <AlwaysOnMobileStickyHeaderFilters />
    </ModalHeader>
    <ModalBody>
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} showBookmark={isLpcPackage} />
      </AccommodationRoomTile>
      <StickyTabButtonsGroup fit="grow">
        <TabButton size="medium" isActive={selectedTab === 'package-options'} onClick={selectPackageOptionsTab}>
          Package options
        </TabButton>
        <TabButton size="medium" isActive={selectedTab === 'about-room'} onClick={selectAboutRoomTab}>
          About the room
        </TabButton>
      </StickyTabButtonsGroup>
      {selectedTab === 'package-options' && <div data-testid="rate-selectedRoom">
        {selectedRoom && <PropertyRoomOptionMobile
          offer={offer}
          bedbankOffer={bedbankOffer}
          displayRates={selectedRoom.displayRates}
          roomFitsCapacity={selectedRoom.roomFitsCapacity}
          datesAvailable={selectedRoom.datesAvailable}
          flightDestination={destinationAirport}
          isHighlighted={selectedRoom.isHighlighted}
          roomRateUnmatched={selectedRoom.roomRateUnmatched}
          roomListId={roomListId}
        />}
        {isLoading && <LoadingIndicator inline />}
      </div>}
      {selectedTab === 'about-room' && <AlwaysOnAboutRoomModalTab pkg={pkg} />}
    </ModalBody>
  </>)
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  selectedRoom: getPropertySelectedRoomCombinedRates(state, appState),
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  airports: appState.geo.airports,
  isLoading: arePropertyRatesLoading(state, appState),
}), SinglePropertyRoomAndPackageDetailsModal)
